import axios from 'axios';
import API_CONFIG from './config';

axios.defaults.baseURL = API_CONFIG.BASE_URL; 
axios.defaults.withCredentials = API_CONFIG.withCredentials; // ensure cookies are sent on every request
axios.defaults.timeout = API_CONFIG.timeout;

export function setupAxiosInterceptors(setUser: Function, navigate: Function) {
    let isRefreshing = false;
    let failedQueue: Array<{ resolve: Function; reject: Function; originalRequest: any }> = [];
    let isLoggedOut = false;
    let lastRefreshAttempt = 0;
    const REFRESH_THROTTLE_MS = 3000; // don't attempt refresh more than once every 3s

    const processQueue = (error: any) => {
        failedQueue.forEach(p => {
            if (error) p.reject(error);
            else p.resolve(axios({ ...p.originalRequest, withCredentials: true }));
        });
        failedQueue = [];
    };

    axios.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            if (!originalRequest) return Promise.reject(error);

            const status = error.response?.status;

            // If we've already logged out the user, immediately reject
            if (isLoggedOut) return Promise.reject(error);

            // Only handle 401 once per request
            if (status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                // If there's no refresh cookie, don't attempt refresh — log out once
                if (!document.cookie.includes('refresh_token=')) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    isLoggedOut = true;
                    try { setUser(null); } catch {}
                    try { navigate('/login'); } catch {}
                    return Promise.reject(error);
                }

                // Throttle refresh attempts to protect backend from bursts
                const now = Date.now();
                if (now - lastRefreshAttempt < REFRESH_THROTTLE_MS) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    isLoggedOut = true;
                    try { setUser(null); } catch {}
                    try { navigate('/login'); } catch {}
                    return Promise.reject(error);
                }
                lastRefreshAttempt = now;

                if (isRefreshing) {
                    // Queue request until current refresh completes
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject, originalRequest });
                    });
                }

                isRefreshing = true;

                try {
                    // Updated to use HTTPS explicitly
                    await axios.post('/api/refresh', {}, { 
                        withCredentials: true,
                        baseURL: API_CONFIG.BASE_URL  // Ensure HTTPS is used
                    });
                    isRefreshing = false;
                    processQueue(null);
                    // Retry original request
                    return axios({ ...originalRequest, withCredentials: true });
                } catch (refreshError) {
                    isRefreshing = false;
                    processQueue(refreshError);
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    isLoggedOut = true;
                    try { setUser(null); } catch {}
                    try { navigate('/login'); } catch {}
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );
}